import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Info, 
  X, 
  ChevronRight, 
  ChevronLeft, 
  CheckCircle2, 
  Lightbulb, 
  BookOpen
} from 'lucide-react';
import { ScreenTourData } from './tourTypes';

interface ScreenTourModalProps {
  isOpen: boolean;
  onClose: () => void;
  tour: ScreenTourData;
}

export default function ScreenTourModal({ isOpen, onClose, tour }: ScreenTourModalProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Previne scroll do body quando o modal estiver aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !mounted) return null;

  const totalSteps = tour.steps.length;
  const currentStep = tour.steps[currentStepIndex];
  const isLastStep = currentStepIndex === totalSteps - 1;

  const handleNext = () => {
    if (isLastStep) {
      setCurrentStepIndex(0);
      onClose();
    } else {
      setCurrentStepIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  const handleClose = () => {
    setCurrentStepIndex(0);
    onClose();
  };

  const modalContent = (
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-6 bg-black/75 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-modal-title"
      onClick={handleClose}
    >
      <div 
        className="bg-white w-full max-w-lg rounded-2xl shadow-2xl border border-zinc-200 overflow-hidden flex flex-col max-h-[88vh] my-auto animate-in fade-in zoom-in-95 duration-150"
        onClick={e => e.stopPropagation()}
      >
        {/* Cabeçalho do Guia - Sempre Visível no Topo */}
        <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 shrink-0">
              <Info className="w-5 h-5 stroke-[2.5]" />
            </div>
            <div>
              <h2 id="tour-modal-title" className="text-base sm:text-lg font-bold leading-tight text-white">
                Como usar esta tela
              </h2>
              <p className="text-xs text-zinc-300 font-medium">
                {tour.screenTitle}
              </p>
            </div>
          </div>
          
          <button
            type="button"
            onClick={handleClose}
            className="px-2.5 py-1.5 text-zinc-300 hover:text-white hover:bg-zinc-800 rounded-xl transition-colors cursor-pointer flex items-center gap-1.5 text-xs font-bold border border-zinc-700"
            aria-label="Fechar guia"
          >
            <span>Fechar</span>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Barra de Progresso Visual */}
        <div className="bg-zinc-100 px-5 py-2.5 border-b border-zinc-200 flex items-center justify-between gap-4 shrink-0">
          <span className="text-xs font-extrabold text-zinc-800 tracking-wide uppercase">
            Passo {currentStepIndex + 1} de {totalSteps}
          </span>
          <div className="flex items-center gap-1.5 flex-1 max-w-[200px]">
            {tour.steps.map((_, idx) => (
              <div
                key={idx}
                className={`h-2.5 flex-1 rounded-full transition-all duration-300 ${
                  idx === currentStepIndex
                    ? 'bg-emerald-600 ring-2 ring-emerald-600/30'
                    : idx < currentStepIndex
                    ? 'bg-emerald-400'
                    : 'bg-zinc-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Conteúdo do Passo Atual - Letra Grande e Clara */}
        <div className="p-5 sm:p-6 overflow-y-auto space-y-4 flex-1">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-zinc-100 border border-zinc-300 text-zinc-900 font-black text-base flex items-center justify-center shrink-0 shadow-2xs">
              {currentStepIndex + 1}
            </div>
            <div className="flex-1">
              <h3 className="text-lg sm:text-xl font-extrabold text-zinc-900 leading-snug">
                {currentStep.title.replace(/^\d+\.\s*/, '')}
              </h3>
            </div>
          </div>

          <p className="text-base sm:text-[17px] text-zinc-700 leading-relaxed font-normal">
            {currentStep.description}
          </p>

          {currentStep.highlight && (
            <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-3.5 flex items-center gap-2.5">
              <BookOpen className="w-5 h-5 text-zinc-600 shrink-0" />
              <span className="text-sm font-semibold text-zinc-800">
                Onde olhar na tela: <span className="text-zinc-950 underline decoration-zinc-400 font-bold">{currentStep.highlight}</span>
              </span>
            </div>
          )}

          {currentStep.tip && (
            <div className="bg-amber-50/90 border border-amber-200 rounded-xl p-3.5 flex items-start gap-2.5 shadow-2xs">
              <Lightbulb className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-950 leading-snug font-medium">
                <strong className="font-bold text-amber-900">Dica prática: </strong>
                {currentStep.tip}
              </div>
            </div>
          )}
        </div>

        {/* Rodapé com Botões de Ação Grandes e Confortáveis - Sempre Visível */}
        <div className="bg-zinc-50 px-5 py-4 border-t border-zinc-200 flex items-center justify-between gap-3 shrink-0">
          <button
            type="button"
            onClick={handlePrev}
            disabled={currentStepIndex === 0}
            className="px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-700 font-bold text-sm hover:bg-white active:scale-95 transition-all disabled:opacity-30 disabled:pointer-events-none cursor-pointer flex items-center gap-1.5 shadow-2xs"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Voltar</span>
          </button>

          <button
            type="button"
            onClick={handleNext}
            className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm active:scale-95 transition-all shadow-md shadow-emerald-600/25 cursor-pointer flex items-center gap-2"
          >
            {isLastStep ? (
              <>
                <CheckCircle2 className="w-4 h-4" />
                <span>Entendi, quero começar!</span>
              </>
            ) : (
              <>
                <span>Próximo Passo</span>
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
